import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { styled } from '@mui/material'
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns'
import { graphql } from 'gatsby'
import rainbowRectangle from '@assets/images/rainbow-rectangle.png'
import useIsFirstRender from '@hooks/useIsFirstRender'

const OuterWrapper = styled('div')(({ theme, DesktopSpacing, MobileSpacing }) => ({
  paddingTop: MobileSpacing?.TopPadding ?? 0,
  paddingBottom: MobileSpacing?.BottomPadding ?? 0,
  [theme.breakpoints.up('md')]: {
    paddingTop: DesktopSpacing?.TopPadding ?? 0,
    paddingBottom: DesktopSpacing?.BottomPadding ?? 0,
  },
}))

const RainbowWrapper = styled('div')(({ theme }) => ({
  background: `url(${rainbowRectangle})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  padding: '2px 2.75px',
  width: 'fit-content',
  borderRadius: '16px',
  filter: 'drop-shadow(0px 3.4817047119140625px 3.4817047119140625px rgba(0, 0, 0, 0.25))',
  [theme.breakpoints.up('md')]: {
    borderRadius: '25.6px',
    padding: '3.5px 4.5px',
  },
}))

const CountdownWrapper = styled('div')(
  ({ theme, backgroundColor, secondaryBackgroundColor, borderColor, isShadow, textColor }) => ({
    display: 'flex',
    background: `linear-gradient(135deg, ${backgroundColor} 45%, ${secondaryBackgroundColor} 45%)`,
    color: textColor,
    padding: '3px 0px',
    width: 'fit-content',
    borderRadius: '14px',
    border: `3px solid ${borderColor}`,
    filter: isShadow ? 'drop-shadow(0px 3.4817047119140625px 3.4817047119140625px rgba(0, 0, 0, 0.25))' : null,
    [theme.breakpoints.up('md')]: {
      padding: '5px 0px',
      borderRadius: '22.4px',
      border: `4.5px solid ${borderColor}`,
    },
  }),
)

const TopBarCountdownWrapper = styled('div')(({ theme, textColor }) => ({
  marginLeft: '30px',
  display: 'flex',
  padding: '3px 0px',
  width: 'fit-content',
  ...(textColor ? { color: textColor } : {}),
  [theme.breakpoints.up('md')]: {
    padding: '5px 0px',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '0',
  },
}))

const TimeSegment = styled('div')(({ theme }) => ({
  width: '51px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    width: '81px',
  },
}))

const TopBarTimeSegment = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  '& > *': {
    marginRight: '4px',
  },
}))

const TimeText = styled('div')(({ theme }) => ({
  fontSize: '43.5px',
  fontFamily: 'League Gothic, sans-serif !important',
  lineHeight: '88%',
  paddingBottom: '2px',
  [theme.breakpoints.up('md')]: {
    fontSize: '69.6px',
    paddingBottom: '2px',
  },
}))

const TopBarTimeText = styled('div')(({ theme }) => ({
  fontSize: '16px',
  lineHeight: 'normal',
  fontWeight: 600,
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
  },
}))

const TimeUnit = styled('div')(({ theme }) => ({
  fontSize: '7.4px',
  fontFamily: 'Montserrat, sans-serif !important',
  lineHeight: 'normal',
  [theme.breakpoints.up('md')]: {
    fontSize: '11.8px',
  },
}))

const TopBarTimeUnit = styled('div')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: 'normal',
  fontWeight: 400,
  [theme.breakpoints.up('md')]: {
    fontSize: '12px',
  },
}))

const formatTwoDigits = num => num.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })

const DEFAULT_BLACK = '#080606'
const DEFAULT_WHITE = '#FFFFFF'

const calculateDuration = expirationDate => {
  const expire = new Date(expirationDate)
  const today = new Date()
  const days = differenceInDays(expire, today)
  const hours = differenceInHours(expire, today) % 24
  const minutes = differenceInMinutes(expire, today) % 60
  const seconds = differenceInSeconds(expire, today) % 60

  return { days, hours, minutes, seconds }
}

const StrapiCountdown = ({ data, isTopBar = false, topBarTextColor }) => {
  const {
    ExpirationDate,
    BackgroundColor,
    SecondaryBackgroundColor,
    BorderColor,
    RainbowOuterBorder,
    TextColor,
    DesktopSpacing,
    MobileSpacing,
    DisplayTime,
    AccessibilityMessage,
  } = data || {}
  const [duration, setDuration] = useState(calculateDuration(ExpirationDate))
  const [loading, setLoading] = useState(true)
  const [isExpired, setIsExpired] = useState(new Date(ExpirationDate) < new Date())
  const isFirstRender = useIsFirstRender()
  const topBarColor = isTopBar && topBarTextColor

  const backgroundColor = useMemo(() => BackgroundColor?.ColorHex ?? DEFAULT_BLACK, [BackgroundColor])
  const secondaryBackgroundColor = useMemo(() => {
    let color = BackgroundColor?.ColorHex ?? DEFAULT_BLACK
    if (SecondaryBackgroundColor?.ColorHex) color = SecondaryBackgroundColor?.ColorHex
    return color
  }, [BackgroundColor, SecondaryBackgroundColor])
  const borderColor = useMemo(() => BorderColor?.ColorHex ?? DEFAULT_BLACK, [BorderColor])
  const textColor = useMemo(() => TextColor?.ColorHex ?? topBarColor ?? DEFAULT_WHITE, [TextColor, topBarColor])

  useEffect(() => {
    const interval = setInterval(() => {
      setLoading(true)
      const newDuration = calculateDuration(ExpirationDate)
      setDuration(newDuration)
      if (new Date(ExpirationDate) < new Date()) {
        setIsExpired(true)
        clearInterval(interval)
      }
      setLoading(false)
    }, 1000)
    return () => clearInterval(interval)
  }, [ExpirationDate])

  if (isExpired || loading) return <></>

  const renderTimeText = interval => {
    if (isFirstRender) return <TimeText>&nbsp;</TimeText>
    return <TimeText>{formatTwoDigits(duration?.[interval])}</TimeText>
  }

  const renderTopBarTimeText = interval => {
    if (isFirstRender) return <TopBarTimeText>&nbsp;</TopBarTimeText>
    return <TopBarTimeText>{duration?.[interval]}</TopBarTimeText>
  }

  const renderTopBarTimeSegments = () => {
    const segments = []

    // Check if DisplayTime includes each time unit, or if DisplayTime is not set (default to all)
    if (!DisplayTime || DisplayTime.includes('Days')) {
      segments.push(
        <TopBarTimeSegment key="days">
          {renderTopBarTimeText('days')}
          <TopBarTimeUnit>d</TopBarTimeUnit>
        </TopBarTimeSegment>,
      )
    }

    if (!DisplayTime || DisplayTime.includes('Hours')) {
      segments.push(
        <TopBarTimeSegment key="hours">
          {renderTopBarTimeText('hours')}
          <TopBarTimeUnit>h</TopBarTimeUnit>
        </TopBarTimeSegment>,
      )
    }

    if (!DisplayTime || DisplayTime.includes('Minutes')) {
      segments.push(
        <TopBarTimeSegment key="minutes">
          {renderTopBarTimeText('minutes')}
          <TopBarTimeUnit>m</TopBarTimeUnit>
        </TopBarTimeSegment>,
      )
    }

    if (!DisplayTime || DisplayTime.includes('Seconds')) {
      segments.push(
        <TopBarTimeSegment key="seconds">
          {renderTopBarTimeText('seconds')}
          <TopBarTimeUnit>s</TopBarTimeUnit>
        </TopBarTimeSegment>,
      )
    }

    return segments
  }
  const renderCountdown = isShadow => (
    <CountdownWrapper
      backgroundColor={backgroundColor}
      secondaryBackgroundColor={secondaryBackgroundColor}
      borderColor={borderColor}
      isShadow={isShadow}
      textColor={textColor}
      aria-hidden="true"
    >
      <TimeSegment>
        {renderTimeText('days')}
        <TimeUnit>DAYS</TimeUnit>
      </TimeSegment>
      <TimeText>:</TimeText>
      <TimeSegment>
        {renderTimeText('hours')}
        <TimeUnit>HOURS</TimeUnit>
      </TimeSegment>
      <TimeText>:</TimeText>
      <TimeSegment>
        {renderTimeText('minutes')}
        <TimeUnit>MINUTES</TimeUnit>
      </TimeSegment>
      <TimeText>:</TimeText>
      <TimeSegment>
        {renderTimeText('seconds')}
        <TimeUnit>SECONDS</TimeUnit>
      </TimeSegment>
    </CountdownWrapper>
  )

  if (isTopBar) {
    return <TopBarCountdownWrapper textColor={textColor}>{renderTopBarTimeSegments()}</TopBarCountdownWrapper>
  }

  return (
    <OuterWrapper DesktopSpacing={DesktopSpacing} MobileSpacing={MobileSpacing}>
      {RainbowOuterBorder && <RainbowWrapper>{renderCountdown()}</RainbowWrapper>}
      {!RainbowOuterBorder && renderCountdown(true)}
    </OuterWrapper>
  )
}

export default StrapiCountdown

StrapiCountdown.propTypes = {
  data: PropTypes.object.isRequired,
  isTopBar: PropTypes.bool,
  topBarTextColor: PropTypes.string,
}

export const countdownFragment = graphql`
  fragment StrapiCountdownFragment on StrapiCountdown {
    Title
    ExpirationDate
    BackgroundColor {
      ColorHex
    }
    SecondaryBackgroundColor {
      ColorHex
    }
    BorderColor {
      ColorHex
    }
    TextColor {
      ColorHex
    }
    RainbowOuterBorder
    DesktopSpacing {
      BottomPadding
      TopPadding
    }
    MobileSpacing {
      BottomPadding
      TopPadding
    }
  }
`
